import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { secureAuthService } from '../services/secureAuthService';
import { secureTeamService } from '../services/secureTeamService';
import { clearAllData } from '../store/teamSlice';
import { UserProfile } from '../lib/supabase';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { performSecurityCleanup } from '../utils/securityCleanup';

interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  planType?: 'free' | 'pro' | 'premium';
  aiChatCount?: number;
}

interface AuthContextType {
  user: User | null;
  supabaseUser: SupabaseUser | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (email: string, password: string, name: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to generate proper UUID format
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Initialize auth state and listen for changes
  useEffect(() => {
    let authInitialized = false;
    
    // Safety mechanism - force loading to false after 5 seconds
    const safetyTimeout = setTimeout(() => {
      console.warn('⚠️ Auth initialization taking too long, forcing loading to false');
      setIsLoading(false);
    }, 5000);
    
    const initializeAuth = async () => {
      console.log('🔄 Starting auth initialization...');
      console.log('🌍 Environment check:', {
        url: process.env.REACT_APP_SUPABASE_URL ? 'SET' : 'MISSING',
        key: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'SET' : 'MISSING'
      });
      
      if (authInitialized) {
        console.log('✅ Auth already initialized, skipping');
        return;
      }
      
      try {
        console.log('🔧 Checking if Supabase is configured...');
        const isConfigured = secureAuthService.isConfigured();
        console.log('📊 Supabase configured?', isConfigured);
        
        if (isConfigured) {
          console.log('✅ Supabase is configured, checking session...');
          const { session } = await secureAuthService.getSession();
          console.log('📋 Initial session check:', session?.user?.email || 'No session');
          
          if (session?.user) {
            console.log('👤 Found active session, signing in user...');
            await handleUserSignIn(session.user);
            authInitialized = true;
          } else {
            console.log('❌ No active session, trying localStorage fallback');
            await attemptLocalStorageSignIn();
          }
        } else {
          console.log('❌ Supabase not configured, using localStorage');
          await attemptLocalStorageSignIn();
        }
      } catch (error) {
        console.error('💥 Error initializing auth:', error);
        // Fallback to localStorage
        await attemptLocalStorageSignIn();
      } finally {
        console.log('🏁 Auth initialization complete, setting loading to false');
        clearTimeout(safetyTimeout);
        setIsLoading(false);
      }
    };
    
    // Listen for auth state changes
    const { data: { subscription } } = secureAuthService.onAuthStateChange(
      async (event, session) => {
        console.log('🔔 Auth state changed:', event, session?.user?.email || 'No user');
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('✅ User signed in via auth state change, updating state');
          await handleUserSignIn(session.user);
          authInitialized = true;
          setIsLoading(false);
        } else if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out, clearing state');
          handleUserSignOut();
          authInitialized = false;
          setIsLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log('🔄 Token refreshed, updating state');
          await handleUserSignIn(session.user);
          setIsLoading(false);
        } else {
          console.log('ℹ️ Other auth event, ensuring loading is false');
          setIsLoading(false);
        }
      }
    );

    // Initialize auth
    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleUserSignIn = async (supabaseUser: SupabaseUser) => {
    console.log('👤 Handling user sign in for:', supabaseUser.email);
    console.log('🔧 Setting supabaseUser state...');
    setSupabaseUser(supabaseUser);
    
    try {
      console.log('📝 Getting user profile from database...');
      // Get user profile from our custom table with timeout
      const profile = await Promise.race([
        secureAuthService.getUserProfile(supabaseUser.id),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Profile loading timeout')), 5000)
        )
      ]);
      console.log('📋 User profile loaded:', profile?.full_name || 'No profile found');
      console.log('🔧 Setting userProfile state...');
      setUserProfile(profile);
      
      // If no profile exists, try to create one
      if (!profile) {
        console.log('🏗️ No profile found, attempting to create one...');
        try {
          await secureAuthService.createUserProfile(
            supabaseUser.id, 
            supabaseUser.email || '', 
            supabaseUser.user_metadata?.full_name || 'User'
          );
          console.log('✅ User profile created successfully');
          
          // Fetch the newly created profile
          const newProfile = await secureAuthService.getUserProfile(supabaseUser.id);
          console.log('📋 Newly created profile:', newProfile?.full_name || 'Failed to fetch');
          setUserProfile(newProfile);
        } catch (createError) {
          console.error('❌ Failed to create user profile:', createError);
          // Continue with basic user info
        }
      }
      
      // Convert to our User format (use updated profile if we created one)
      const finalProfile = profile || (await secureAuthService.getUserProfile(supabaseUser.id));
      const user: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        name: finalProfile?.full_name || supabaseUser.user_metadata?.full_name || 'User',
        createdAt: supabaseUser.created_at,
        planType: finalProfile?.plan_type || 'free',
        aiChatCount: finalProfile?.ai_chat_count || 0
      };
      
      console.log('✅ Setting user state:', { name: user.name, email: user.email, id: user.id });
      setUser(user);
      console.log('🎉 User state set successfully!');
      
      // Try to migrate team data from localStorage
      if (profile) {
        console.log('📦 Attempting team data migration...');
        await secureTeamService.migrateFromLocalStorage(supabaseUser.id);
      }
    } catch (error) {
      console.error('💥 Error in handleUserSignIn:', error);
      
      // Even if profile loading fails, set basic user info
      const basicUser: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        name: supabaseUser.user_metadata?.full_name || supabaseUser.email || 'User',
        createdAt: supabaseUser.created_at,
        planType: 'free',
        aiChatCount: 0
      };
      
      console.log('🔄 Setting fallback user state:', { name: basicUser.name, email: basicUser.email });
      setUser(basicUser);
      console.log('✅ Fallback user state set!');
    }
  };

  const handleUserSignOut = () => {
    setUser(null);
    setSupabaseUser(null);
    setUserProfile(null);
    // Clear all team data and notifications
    dispatch(clearAllData());
  };

  const attemptLocalStorageSignIn = async () => {
    try {
      console.log('🔒 SECURITY: Performing security cleanup on app startup...');
      
      // Perform comprehensive security cleanup
      performSecurityCleanup();
      
      console.log('✅ Security cleanup completed');
    } catch (error) {
      console.error('💥 Error during security cleanup:', error);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    console.log('🔐 Login attempt started for:', email);
    setIsLoading(true);
    
    try {
      if (secureAuthService.isConfigured()) {
        console.log('✅ Using Supabase authentication');
        // Use Supabase auth only - NO localStorage fallback for security
        const { user: supabaseUser, error } = await secureAuthService.signIn(email, password);
        
        if (error) {
          console.error('❌ Supabase login error:', error.message);
          setIsLoading(false);
          return false;
        }
        
        if (supabaseUser) {
          console.log('✅ Supabase login successful, auth state change will handle the rest');
          // Don't set loading to false here - let the auth state change handler do it
          return true;
        }
        
        setIsLoading(false);
        return false;
      } else {
        console.error('❌ Supabase not configured - authentication disabled for security');
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error('💥 Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const signup = async (email: string, password: string, name: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      if (secureAuthService.isConfigured()) {
        console.log('✅ Using Supabase authentication for signup');
        // Use Supabase auth only - NO localStorage fallback for security
        const { user: supabaseUser, error } = await secureAuthService.signUp(email, password, name);
        
        if (error) {
          console.error('❌ Supabase signup error:', error.message);
          setIsLoading(false);
          return false;
        }
        
        if (supabaseUser) {
          console.log('✅ Supabase signup successful, auth state change will handle the rest');
          // Don't set loading to false here - let the auth state change handler do it
          return true;
        }
        
        setIsLoading(false);
        return false;
      } else {
        console.error('❌ Supabase not configured - authentication disabled for security');
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error('💥 Signup error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = async () => {
    if (secureAuthService.isConfigured() && supabaseUser) {
      await secureAuthService.signOut();
      // The auth state change will trigger handleUserSignOut which clears data
    } else {
      // Clear any remaining localStorage data for security
      performSecurityCleanup();
      setUser(null);
      dispatch(clearAllData());
    }
  };

  const refreshProfile = async () => {
    if (supabaseUser) {
      const profile = await secureAuthService.getUserProfile(supabaseUser.id);
      setUserProfile(profile);
      
      if (profile) {
        setUser(prev => prev ? {
          ...prev,
          planType: profile.plan_type,
          aiChatCount: profile.ai_chat_count
        } : null);
      }
    }
  };

  const value: AuthContextType = {
    user,
    supabaseUser,
    userProfile,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    isLoading,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 